import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Form, { type FormItem, type FormRefType } from '#/common/components/Form';
import Modal from '#/common/components/Modal/Modal';
import TMMButton from '#/common/components/TMMButton';
import assetPlanFields from '#/modules/asset-plan/utils/fields';
import useCreateAssetPlanFromAssetsMutation from '#/modules/asset/hooks/useCreateAssetPlanFromAssetsMutation';
import useToast from '#/common/hooks/useToast';
import styled from 'styled-components';
import type { FieldDataChangedEvent } from 'devextreme/ui/form';
import { useNavigate } from 'react-router-dom';
import type { TenantSummary } from '#/modules/asset/components/AssetDataGrid/AssetDataGrid';
import { color } from '@nuvolo/nuux-theme/utils';
import normalizeYearValue from '#/common/utils/parsers/normalizeYearValue';

type CreateAssetPlanFormData = {
  organization?: string;
  name?: string;
  startYear?: number | Date;
  endYear?: number | Date;
  notes?: string;
};

export interface AssetPlanScope {
  includedAssetCount: number;
  selectedAssetIds: string[];
  selectedTenantSummary?: TenantSummary;
  scopeCondition?: string;
  invalidStatusCount: number;
}

export interface CreateAssetPlanFormProps {
  visible: boolean;
  scope: AssetPlanScope;
  onClose: () => void;
}

const ModalTitle = styled.div`
  display: flex;
  flex-direction: column;
`;

const ModalSubtitle = styled.div`
  margin-top: 4px;
  color: ${color('custom', 'constantGray')};
  font-size: 12px;
  font-weight: 400;
`;

const WarningMessage = styled.div`
  border: 1px solid ${color('warn', 'middle')};
  background-color: ${color('warn', 'middle', 0.05)};
  color: ${color('primary', 'dark')};
  font-family: 'Inter', Arial, sans-serif;
  font-size: 16px;
  font-style: normal;
  line-height: 100%;
  border-radius: 16px;
  padding: 12px 24px;
  margin-bottom: 16px;
`;

const CreateAssetPlanForm: React.FC<CreateAssetPlanFormProps> = ({
  visible,
  scope: {
    includedAssetCount,
    selectedAssetIds,
    selectedTenantSummary,
    scopeCondition,
    invalidStatusCount,
  },
  onClose,
}) => {
  const navigate = useNavigate();

  const { alert } = useToast();
  const { createAssetPlanFromAssetsMutation } = useCreateAssetPlanFromAssetsMutation();
  const formRef = useRef<FormRefType | null>(null);
  const [formData, setFormData] = useState<CreateAssetPlanFormData>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const organizationName = useMemo(
    () => selectedTenantSummary?.name || selectedTenantSummary?.id || 'Not available',
    [selectedTenantSummary],
  );
  const selectedAssetsLabel = useMemo(
    () => `${includedAssetCount} assets selected`,
    [includedAssetCount],
  );
  const currentYear = useMemo(() => new Date().getFullYear(), []);
  const formRecord = useMemo(
    () => ({
      ...formData,
      organization: organizationName,
    }),
    [formData, organizationName],
  );

  useEffect(() => {
    if (visible && invalidStatusCount > 0)
      alert(
        'warning',
        `${invalidStatusCount} selected asset(s) have invalid statuses and will be excluded from the asset plan. Only in-stock and installed assets will be included in the plan.`,
      );
  }, [invalidStatusCount, visible]);

  const createAssetPlanFields = useMemo<FormItem<CreateAssetPlanFormData>[]>(
    () => [
      {
        colCount: 2,
        cssClass: 'tmm-section tmm-section-no-reset-margin-padding',
        items: [
          {
            dataField: 'organization',
            label: 'Organization',
            editorType: 'TextBox',
            editorOptions: {
              readOnly: true,
            },
          },
          {
            ...assetPlanFields.name,
            isRequired: true,
          },
          {
            ...assetPlanFields.startYear,
            isRequired: true,
            editorType: 'DateBox',
            editorOptions: {
              type: 'date',
              pickerType: 'calendar',
              displayFormat: 'yyyy',
              openOnFieldClick: true,
              useMaskBehavior: true,
              calendarOptions: {
                minZoomLevel: 'decade',
                maxZoomLevel: 'decade',
              },
              min: new Date(currentYear, 0, 1),
              max: new Date(currentYear + 20, 11, 31),
              placeholder: 'Select start year',
            },
          },
          {
            ...assetPlanFields.endYear,
            isRequired: true,
            editorType: 'DateBox',
            editorOptions: {
              type: 'date',
              pickerType: 'calendar',
              displayFormat: 'yyyy',
              openOnFieldClick: true,
              useMaskBehavior: true,
              calendarOptions: {
                minZoomLevel: 'decade',
                maxZoomLevel: 'decade',
              },
              min: new Date(currentYear, 0, 1),
              max: new Date(currentYear + 20, 11, 31),
              placeholder: 'Select end year',
            },
          },
          {
            ...assetPlanFields.notes,
            colSpan: 2,
          },
        ],
      },
    ],
    [currentYear],
  );

  const isConfirmDisabled =
    !formData.name?.trim() ||
    normalizeYearValue(formData.startYear) === undefined ||
    normalizeYearValue(formData.endYear) === undefined;

  useEffect(() => {
    if (!visible) {
      setFormData({});
    }
  }, [visible]);

  const handleFieldDataChanged = useCallback((e: FieldDataChangedEvent) => {
    setFormData((current) => ({
      ...current,
      [e.dataField as keyof CreateAssetPlanFormData]: e.value,
    }));
  }, []);

  const handleConfirm = useCallback(async () => {
    const instance = formRef.current?.instance();
    const validation = instance?.validate();

    if (!validation?.isValid) {
      return;
    }

    const formData = (instance?.option('formData') ?? {}) as CreateAssetPlanFormData;
    const normalizedStartYear = normalizeYearValue((formData.startYear as Date)?.getFullYear());
    const normalizedEndYear = normalizeYearValue((formData.endYear as Date)?.getFullYear());

    if (!formData.name || normalizedStartYear === undefined || normalizedEndYear === undefined) {
      return;
    }

    if (normalizedEndYear < normalizedStartYear) {
      await alert('error', 'End Year cannot be before Start Year');
      return;
    }

    try {
      setIsSubmitting(true);
      const createdAssetPlan = await createAssetPlanFromAssetsMutation({
        name: formData.name.trim(),
        startYear: normalizedStartYear,
        endYear: normalizedEndYear,
        notes: formData.notes?.trim() || undefined,
        selectedItems: selectedAssetIds.length > 0 ? selectedAssetIds : undefined,
        scopeCondition,
      });

      const createdAssetPlanId = createdAssetPlan?.id;

      if (!createdAssetPlanId) {
        await alert('error', 'Asset plan was created, but no ID was returned for navigation.');
        onClose();
        return;
      }

      // Clear form dirty state after successful creation to prevent unsaved changes prompt when navigating to the new asset plan page
      formRef.current?.instance()?.clearDirty?.();
      onClose();
      navigate(`/asset-plans/${createdAssetPlanId}/items`);

      void alert('success', 'Asset plan created successfully');
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to create asset plan';
      await alert('error', message);
    } finally {
      setIsSubmitting(false);
    }
  }, [
    createAssetPlanFromAssetsMutation,
    selectedAssetIds,
    scopeCondition,
    alert,
    onClose,
    navigate,
  ]);

  return (
    <Modal
      visible={visible}
      title={
        <ModalTitle>
          <div>Create Asset Plan</div>
          <ModalSubtitle>{selectedAssetsLabel}</ModalSubtitle>
        </ModalTitle>
      }
      dragEnabled
      width={760}
      onHiding={onClose}
      content={
        <div>
          <WarningMessage>The years cannot be edited after creation</WarningMessage>
          <Form<CreateAssetPlanFormData>
            key={visible ? 'create-asset-plan-open' : 'create-asset-plan-closed'}
            ref={formRef}
            record={formRecord}
            fields={createAssetPlanFields}
            onFieldDataChanged={handleFieldDataChanged}
          />
        </div>
      }
      actions={[
        <TMMButton
          className="width-50"
          key="cancel-create-asset-plan"
          label="Cancel"
          type="normal"
          onClick={onClose}
        />,
        <TMMButton
          className="width-50"
          key="confirm-create-asset-plan"
          label="Create"
          type="submit"
          disabled={isConfirmDisabled || isSubmitting}
          onClick={handleConfirm}
        />,
      ]}
    />
  );
};

export default React.memo(CreateAssetPlanForm);
